/**
 * User Preference Schemas
 *
 * The shape of the comprehensive per-user preferences store (the `UserPreference`
 * table) and the per-namespace Zod registry that validates writes at the API
 * boundary.
 *
 * Each preference row is keyed (schoolUserId, namespace, key) with an optional
 * JSON `value`. A namespace declares the schema for its `key` and `value` in the
 * registry below; an unknown namespace, an unrecognized key, or a value that
 * fails the namespace's value schema is rejected (AE4).
 *
 * v1 registers exactly two set-membership namespaces (value is null — a row's
 * existence is membership):
 *   - "home.widgets"  — key = a home-screen widget key
 *   - "home.children" — key = a student publicId
 *
 * The registry is structured as per-namespace entries so adding a namespace is
 * an append here, not a monolith edit.
 */

import { z } from 'zod'

// --- Widget keys (stable, exported for mobile to import from shared-types) ---

/**
 * Stable home-screen widget keys. Copied from the mobile widget registry so the
 * `home.widgets` key schema can validate against the exact known set. Mobile
 * will import this from shared-types rather than maintaining a local copy.
 */
export const HOME_WIDGET_KEYS = z.enum([
  'welcome-card',
  'home-screen-messages',
  'faculty-next-class-widget',
  'students-for-parents-widget',
  'tile-widget',
  // Student-role home widgets (SCH-577). Kept in sync with the mobile registry
  // const via the `satisfies` drift guard in widget-registry.core.ts.
  'student-day-widget',
  'student-assignments-widget',
  'student-events-widget'
])

export type HomeWidgetKey = z.infer<typeof HOME_WIDGET_KEYS>

// --- Namespaces ---

/** Every preference namespace registered in v1. Adding a namespace = append. */
export const USER_PREFERENCE_NAMESPACES = [
  'home.widgets',
  'home.children',
  // VALUE-BEARING namespace (plan #001, KTD14): the admissions CRM "what
  // changed" feed last-visit marker. Unlike the set-membership namespaces, this
  // one stores a JSON `value` (an ISO timestamp) — see the registry below.
  'admissions'
] as const

export type UserPreferenceNamespace = (typeof USER_PREFERENCE_NAMESPACES)[number]

export const UserPreferenceNamespaceSchema = z.enum(USER_PREFERENCE_NAMESPACES)

/**
 * The SET-MEMBERSHIP namespaces only (a row's existence is membership; `value`
 * is NULL). VALUE-BEARING namespaces (e.g. `admissions`, KTD14) are excluded so
 * their JSON-valued marker rows never bleed into the set-membership LIST
 * contract — the list use-case enumerates THIS set, not every namespace.
 */
export const SET_MEMBERSHIP_PREFERENCE_NAMESPACES = [
  'home.widgets',
  'home.children'
] as const satisfies readonly UserPreferenceNamespace[]

export type SetMembershipPreferenceNamespace = (typeof SET_MEMBERSHIP_PREFERENCE_NAMESPACES)[number]

// --- Per-namespace key / value schemas ---

/**
 * A set-membership key: a non-empty string. Used by both v1 namespaces — for
 * "home.widgets" it is a widget key, for "home.children" it is a student
 * publicId. Shape-validation only: it does NOT prove the caller may reference
 * that key (e.g. relationship integrity for a student publicId is enforced
 * separately at the API layer).
 */
const SetMembershipKeySchema = z.string().min(1)

/**
 * A set-membership value: null/undefined only. Existence of the row is the
 * membership signal; a non-null value is rejected.
 */
const SetMembershipValueSchema = z.null().optional()

/**
 * The admissions namespace key set (plan #001, KTD14). Currently the single
 * feed last-visit marker; an enum so an unknown admissions key is rejected.
 */
export const ADMISSIONS_PREFERENCE_KEYS = z.enum(['lastVisitedAt'])
export type AdmissionsPreferenceKey = z.infer<typeof ADMISSIONS_PREFERENCE_KEYS>

/**
 * VALUE-BEARING value schema for the admissions `lastVisitedAt` marker (KTD14):
 * an object carrying an ISO-8601 `lastVisitedAt` instant. Distinct from the
 * set-membership null-value namespaces — this namespace stores a JSON value.
 */
export const AdmissionsMarkerValueSchema = z.object({
  lastVisitedAt: z.string().datetime()
})
export type AdmissionsMarkerValue = z.infer<typeof AdmissionsMarkerValueSchema>

/** Schema pair for a single namespace. */
export interface UserPreferenceNamespaceSchemas {
  keySchema: z.ZodType<string>
  valueSchema: z.ZodType<unknown>
}

/**
 * Registry: namespace -> { keySchema, valueSchema }.
 *
 * One entry per namespace. The API boundary looks the namespace up here and
 * parses the incoming key/value with these schemas; an absent namespace is an
 * unknown-namespace rejection.
 */
export const USER_PREFERENCE_REGISTRY: Record<UserPreferenceNamespace, UserPreferenceNamespaceSchemas> = {
  'home.widgets': {
    keySchema: HOME_WIDGET_KEYS,
    valueSchema: SetMembershipValueSchema
  },
  'home.children': {
    keySchema: SetMembershipKeySchema,
    valueSchema: SetMembershipValueSchema
  },
  // Value-bearing (KTD14): the key is the marker name, the value an ISO instant.
  admissions: {
    keySchema: ADMISSIONS_PREFERENCE_KEYS,
    valueSchema: AdmissionsMarkerValueSchema
  }
}

// --- Row shape ---

/** A single stored preference row, as surfaced in API responses. */
export const UserPreferenceRowSchema = z.object({
  namespace: UserPreferenceNamespaceSchema,
  key: z.string().min(1),
  value: z.unknown().nullable()
})

export type UserPreferenceRow = z.infer<typeof UserPreferenceRowSchema>

// --- Registry helpers ---

/** Is the namespace one we know about? */
export function isKnownUserPreferenceNamespace(namespace: string): namespace is UserPreferenceNamespace {
  return (USER_PREFERENCE_NAMESPACES as readonly string[]).includes(namespace)
}

/**
 * Validate a (namespace, key, value) triple against the registry. Returns a
 * discriminated result so callers (the write handler) can reject unknown
 * namespaces, unrecognized keys, and bad values at the API boundary (AE4).
 */
export function validateUserPreference(input: { namespace: string; key: unknown; value?: unknown }):
  | {
      ok: true
      namespace: UserPreferenceNamespace
      key: string
      value: unknown
    }
  | {
      ok: false
      reason: 'unknown_namespace' | 'invalid_key' | 'invalid_value'
    } {
  if (!isKnownUserPreferenceNamespace(input.namespace)) {
    return { ok: false, reason: 'unknown_namespace' }
  }

  const { keySchema, valueSchema } = USER_PREFERENCE_REGISTRY[input.namespace]

  const keyResult = keySchema.safeParse(input.key)
  if (!keyResult.success) {
    return { ok: false, reason: 'invalid_key' }
  }

  const valueResult = valueSchema.safeParse(input.value)
  if (!valueResult.success) {
    return { ok: false, reason: 'invalid_value' }
  }

  return {
    ok: true,
    namespace: input.namespace,
    key: keyResult.data,
    value: valueResult.data
  }
}
